import { inject, Injectable, OnDestroy, effect, Signal } from '@angular/core';
import { interval, Subscription, throttle } from 'rxjs';

import { StateService } from '../../common/state.service';
import { ILogger } from '../../core/observability/logger';
import { MDB_LOGGER_FACTORY } from '../../core/observability/provider';
import { AuthStatusType } from '../auth/auth-state.service';
import { AuthService } from '../auth/auth.service';
import { MDB_AUTH_STATUS } from '../auth/tokens';

export interface IdleState {
  isIdle: boolean;
  lastActivityDate: Date;
}

const USER_ACTIVITY_EVENTS = [
  'keydown',
  'click',
  'mousedown',
  'mousemove',
  'scroll',
];

const IDLE_TIMEOUT = 900; // seconds - 900 for 15mins
const IDLE_CHECK_INTERVAL = 60; // seconds

const IDLE_ERROR =
  'Your session has expired due to inactivity and you have been automatically logged out.';

@Injectable({
  providedIn: 'root',
})
export class IdleService extends StateService<IdleState> implements OnDestroy {
  private logger: ILogger;

  private idleSubscription?: Subscription;

  private authService: AuthService;
  private authStatus: Signal<AuthStatusType>;

  constructor() {
    super({ isIdle: false, lastActivityDate: new Date() } as IdleState);

    this.logger = inject(MDB_LOGGER_FACTORY)('IdleService');
    this.authService = inject(AuthService);
    this.authStatus = inject(MDB_AUTH_STATUS);

    const handler = () => this.updateStatus(false);

    USER_ACTIVITY_EVENTS.forEach(event => {
      window.addEventListener(event, handler);
    });

    effect(() => {
      const isIdle = this.select('isIdle')();
      if (isIdle) {
        localStorage.setItem('idleMessage', IDLE_ERROR);
        void this.authService.logout('Idle Session Timeout');
      }
    });

    effect(() => {
      const status = this.authStatus();

      this.logger.debug('Authentication Status Changed', { status: status });

      if (status === AuthStatusType.Anonymous) {
        this.stopWatching();
        return;
      }

      this.startWatching();
    });
  }

  /**
   * Read any stored message that may persist in browser storage when an idle session is logged out and remove any
   * stored message from the browser storage.
   *
   * @returns(string | null) - Returns either the stored message or null if no message exists.
   *
   * @usageNotes
   * The method is intended to be called once from the LoginComponent, which helps guarantee that the idle message is
   * only shown once.
   */
  public takeAndForget(): string | null {
    const message = localStorage.getItem('idleMessage');
    localStorage.removeItem('idleMessage');
    return message;
  }

  private get lastActivityDate() {
    return this.select('lastActivityDate')().getTime();
  }

  private startWatching(): void {
    // Do an initial validation and then validate on an interval. This guarantees the token is valid
    // as soon as possible and avoids race conditions.
    this.authService.validateToken();

    this.updateStatus(false);

    this.idleSubscription = interval(IDLE_CHECK_INTERVAL * 1000)
      .pipe(throttle(() => interval(1000)))
      .subscribe(() => {
        const now = new Date().getTime();

        if (now - this.lastActivityDate > IDLE_TIMEOUT * 1000) {
          this.updateStatus(true);
          return;
        }

        if (now - this.lastActivityDate <= IDLE_CHECK_INTERVAL * 2001) {
          this.authService.validateToken();
        }
      });
  }

  private updateStatus(isIdle: boolean) {
    if (isIdle) {
      this.update({ isIdle: true });
      return;
    }

    this.update({ isIdle: false, lastActivityDate: new Date() });
  }

  private stopWatching() {
    if (this.idleSubscription) {
      this.idleSubscription.unsubscribe();
    }
  }

  public ngOnDestroy() {
    this.stopWatching();
  }
}
